import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { Alert, Button, Card, Form, Input, Popconfirm, Space, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { APPLICATIONS } from "@zhihaoscm/meta";
import { dateTimeFormat } from "@zhihaoscm/utils";
import { useUserStore } from "@/views/stores";
import type { ApplicationPageResult, ApplicationQuery, ApplicationRow } from "./types";

export interface ApplicationPageProps {
    copyAccessKey?: (value: string) => Promise<void>;
    deleteApplication?: (id: number | string) => Promise<void>;
    getSecretKey?: (id: number | string) => Promise<string>;
    loadApplications?: (query: ApplicationQuery) => Promise<ApplicationPageResult>;
    permissions?: string[];
}

// 旧 Vue useTable 的默认 pageable.size 为 10，保持应用列表首屏请求契约一致。
const defaultQuery: ApplicationQuery = { page: 1, size: 10, sortKey: "created_time", sortOrder: "desc" };

/** 将 Ant Table 排序方向转换为 Vue 应用分页接口的 sortKey/sortOrder。 */
export function buildApplicationSortQuery(
    query: ApplicationQuery,
    order?: "ascend" | "descend" | null
): ApplicationQuery {
    return {
        ...query,
        page: 1,
        sortKey: "created_time",
        sortOrder: order === "ascend" ? "asc" : order === "descend" ? "desc" : defaultQuery.sortOrder
    };
}

/** 将 Vue 应用功能点编码映射为用户可读的中文名称。 */
const formatApplicationNames = (items?: string[]) => {
    const names = (items || []).map(code => APPLICATIONS.getIntroByCode(code)).filter(Boolean);
    return names.length ? names.join("、") : DEFAULT_VALUE;
};

/** 对齐 Vue 应用列表的 dateTimeFormat 和空值处理。 */
const formatApplicationTime = (value?: string) => (value ? dateTimeFormat(value) : DEFAULT_VALUE);

/** 统一应用列表文本列的空字符串缺省值。 */
const formatApplicationValue = (value?: string | null) => value || DEFAULT_VALUE;

/** 复用旧 Vue `/application/paging` 接口的应用列表请求。 */
export async function loadApplications(query: ApplicationQuery): Promise<ApplicationPageResult> {
    const response = await request.service.get<ApplicationPageResult>("/application/paging", { params: query });
    return response.data;
}

/** 复用旧 Vue 应用凭证接口，只有显式点击查看时才发起请求。 */
export async function getApplicationSecretKey(id: number | string): Promise<string> {
    const response = await request.service.get<string>(`/application/get-sk/${id}`);
    return response.data;
}

/** 复用旧 Vue 应用删除接口。 */
export async function deleteApplication(id: number | string): Promise<void> {
    await request.service.delete(`/application/${id}`);
}

/** 复制 AK 的浏览器能力，测试环境可注入稳定实现。 */
export async function copyApplicationAccessKey(value: string): Promise<void> {
    await navigator.clipboard?.writeText(value);
}

/** 平台管理-应用管理页面，先覆盖列表读取和核心凭证字段展示。 */
export function ApplicationPage({
    copyAccessKey = copyApplicationAccessKey,
    deleteApplication: remove = deleteApplication,
    getSecretKey = getApplicationSecretKey,
    loadApplications: load = loadApplications,
    permissions
}: ApplicationPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canWrite = (permissions || storePermissions).includes("ROLE_APPLICATION");
    const [applications, setApplications] = useState<ApplicationRow[]>([]);
    const [total, setTotal] = useState(0);
    const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
    const [secretValues, setSecretValues] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<ApplicationQuery>(defaultQuery);
    const [filterForm] = Form.useForm<{ param?: string }>();

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setApplications(result.list || []);
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("应用加载失败");
                    setApplications([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将 Vue 应用管理的关键词筛选转换为 `param` 查询参数。
    const submitFilters = (values: { param?: string }) => {
        setErrorMessage(null);
        setQuery({ ...defaultQuery, ...(values.param ? { param: values.param } : {}) });
    };

    // 清空关键词并恢复 Vue 默认排序分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    /** 写操作后重新读取当前分页，保留旧 Vue 表格刷新语义。 */
    const reloadApplications = useCallback(async () => {
        const result = await load(query);
        setApplications(result.list || []);
        setTotal(result.total || 0);
    }, [load, query]);

    const toggleSecret = useCallback(
        async (record: ApplicationRow) => {
            const id = record.id;
            if (id == null) return;
            const key = String(id);
            if (visibleSecrets[key]) {
                setVisibleSecrets(current => ({ ...current, [key]: false }));
                return;
            }
            try {
                const value = await getSecretKey(id);
                setErrorMessage(null);
                setSecretValues(current => ({ ...current, [key]: value }));
                setVisibleSecrets(current => ({ ...current, [key]: true }));
            } catch {
                setErrorMessage("应用 SK 获取失败");
            }
        },
        [getSecretKey, visibleSecrets]
    );

    const handleCopyAccessKey = useCallback(
        async (value: string) => {
            try {
                await copyAccessKey(value);
                setErrorMessage(null);
            } catch {
                setErrorMessage("应用 AK 复制失败");
            }
        },
        [copyAccessKey]
    );

    const handleDelete = useCallback(
        async (id: number | string | undefined) => {
            if (id == null) return;
            try {
                // 删除或刷新失败时保留当前应用列表，并展示可恢复的 Vue 兼容错误提示。
                await remove(id);
                await reloadApplications();
                setErrorMessage(null);
            } catch {
                setErrorMessage("应用删除失败");
            }
        },
        [reloadApplications, remove]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue ZhTable 的字段和 slot 行为，避免每次请求后重建列状态。
    const applicationColumns = useMemo<TableColumnProps<ApplicationRow>[]>(
        () => [
            {
                key: "id",
                label: "应用ID",
                prop: "id",
                width: 88,
                render: ({ row }) => (
                    <Button
                        aria-label="查看应用详情"
                        type="link"
                        onClick={() => void navigate(`/manageCenter/appManagement/applicationDetail/${row.id || ""}`)}
                    >
                        {row.id ?? DEFAULT_VALUE}
                    </Button>
                )
            },
            {
                key: "appName",
                label: "应用名称",
                prop: "appName",
                minWidth: 198,
                render: ({ cellValue }) => formatApplicationValue(cellValue as string | undefined)
            },
            {
                key: "accessKey",
                label: "AK(access key)",
                prop: "accessKey",
                width: 350,
                render: ({ cellValue }) => (
                    <div className="flex items-center gap-2">
                        <span>{formatApplicationValue(cellValue as string | undefined)}</span>
                        {canWrite ? (
                            <Button
                                aria-label="复制 AK"
                                type="link"
                                onClick={() => void handleCopyAccessKey((cellValue as string | undefined) || "")}
                            >
                                复制
                            </Button>
                        ) : null}
                    </div>
                )
            },
            {
                key: "secretKey",
                label: "SK(secret key)",
                prop: "secretKey",
                showOverflowTooltip: false,
                width: 350,
                render: ({ row, cellValue }) => {
                    const key = String(row.id || row.appName || "application");
                    return (
                        <div className="flex items-center gap-2">
                            <span>
                                {visibleSecrets[key]
                                    ? formatApplicationValue(secretValues[key])
                                    : formatApplicationValue(cellValue as string | undefined)}
                            </span>
                            {canWrite ? (
                                <Button aria-label="查看 SK" type="link" onClick={() => void toggleSecret(row)}>
                                    {visibleSecrets[key] ? "隐藏 SK" : "查看 SK"}
                                </Button>
                            ) : null}
                        </div>
                    );
                }
            },
            {
                key: "applications",
                label: "关联的功能点",
                prop: "applications",
                minWidth: 300,
                render: ({ cellValue }) => formatApplicationNames(cellValue as string[] | undefined)
            },
            {
                key: "createdTime",
                label: "创建时间",
                prop: "createdTime",
                minWidth: 100,
                sortable: "custom",
                render: ({ cellValue }) => formatApplicationTime(cellValue as string | undefined)
            },
            ...(canWrite
                ? [
                      {
                          key: "operation",
                          label: "操作",
                          prop: "operation",
                          fixed: "right" as const,
                          render: ({ row }: { row: ApplicationRow }) => (
                              <div className="flex gap-2">
                                  <Button
                                      disabled={banButton}
                                      type="link"
                                      onClick={() =>
                                          void navigate(`/manageCenter/appManagement/applicationEdit/${row.id || ""}`)
                                      }
                                  >
                                      编辑
                                  </Button>
                                  <Popconfirm
                                      cancelText="取消"
                                      okText="确定"
                                      title="确认删除该应用吗？"
                                      onConfirm={() => void handleDelete(row.id)}
                                  >
                                      <Button disabled={banButton} type="link">
                                          删除
                                      </Button>
                                  </Popconfirm>
                              </div>
                          )
                      }
                  ]
                : [])
        ],
        [banButton, canWrite, handleCopyAccessKey, handleDelete, navigate, secretValues, toggleSecret, visibleSecrets]
    );

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="application-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ApplicationRow>
                    className="flex-1"
                    columns={normalizeOperationColumns(applicationColumns, applications)}
                    data={applications}
                    pagination={false}
                    defaultSort={{ prop: "createdTime", order: "descending" }}
                    onSortChange={({ prop, order }) => {
                        if (prop !== "createdTime") return;
                        setQuery(currentQuery =>
                            buildApplicationSortQuery(
                                currentQuery,
                                order === "ascending" ? "ascend" : order === "descending" ? "descend" : null
                            )
                        );
                    }}
                    rowKey={record => record.id || record.appName || "application"}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                应用管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/manageCenter/appManagement/applicationAdd")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item name="param">
                                    <Input
                                        aria-label="应用名称或ID"
                                        placeholder="请输入应用名称或者应用ID搜索"
                                        style={{ width: 260 }}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button
                                        aria-label="查询应用"
                                        htmlType="submit"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                    >
                                        搜索
                                    </Button>
                                    <Button
                                        aria-label="重置"
                                        icon={<TableSearchIcon name="reset" />}
                                        onClick={resetFilters}
                                    />
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧 Vue Element 分页器的默认可选条数。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default ApplicationPage;
